import { useCallback, useEffect, useRef, useState } from 'react';

const useDrag = (): [React.RefObject<HTMLDivElement>, boolean] => {
  const offset = useRef([0, 0]);
  const draggable = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = useCallback(
    (event) => {
      event.preventDefault();

      setIsDragging(true);
      const [offsetX, offsetY] = offset.current;
      const { current } = draggable;
      if (!current) return;
      current.style.top = `${event.clientY - offsetY}px`;
      current.style.left = `${event.clientX - offsetX}px`;
      current.style.right = 'unset';
      current.style.bottom = 'unset';

      return false;
    },
    [offset, draggable]
  );

  const handleMouseUp = useCallback(() => {
    document.documentElement.removeEventListener('mousemove', handleMove);
    document.documentElement.removeEventListener('mouseup', handleMouseUp);

    // delay state changing to prevent onClick firing
    setTimeout(() => setIsDragging(false), 0);
  }, [handleMove]);

  const handleMouseDown = useCallback(
    (event) => {
      if (!draggable.current) return;
      const { left, top } = draggable.current.getBoundingClientRect();
      offset.current = [event.clientX - left, event.clientY - top];
      document.documentElement.addEventListener('mousemove', handleMove);
      document.documentElement.addEventListener('mouseup', handleMouseUp);

      return false;
    },
    [handleMove, handleMouseUp, draggable]
  );

  useEffect(() => {
    if (!draggable.current) return;
    draggable.current.addEventListener('mousedown', handleMouseDown);

    return () => {
      if (!draggable.current) return;
      draggable.current.removeEventListener('mousedown', handleMouseDown);
    };
  }, [draggable.current, handleMouseDown]);

  return [draggable, isDragging];
};

export default useDrag;
